import { useCallback } from 'react';
import { useNavigation } from '@react-navigation/native';

import { useNavigateToLastOrderMenu, useSweetpassTier } from '@order/hooks';
import { useFeatureFlag } from '@order/LaunchDarkly';
import { useTelemetry } from '@order/Telemetry';

/**
 * Handler for the "Home Hero CTA".
 *
 * view_menu          | opens the last order's menu
 * upgrade_sweetpass  | takes you to upgrade screen
 * explore_benefits   | takes you to sweetpass benefits screen
 * explore_challenges | takes you to sweetpass home and scrolls you down to challenges section
 * explore_rewards    | takes you to sweetpass home and scrolls you down to rewards section
 *
 * @see {@link https://sweetgreen.atlassian.net/browse/CELS-675}
 */

export const useHomeHeroCTA = (action?: HomeHeroAction) => {
  const handleViewMenu = useNavigateToLastOrderMenu();
  const navigation = useNavigation();
  const { track } = useTelemetry();

  // ─── Sweetpass ───────────────────────────────────────────────────────

  const isSweetpassEnabled = useFeatureFlag('OPS-sweetpass-enabled');
  const { sweetpassTierName, isSweetpassTierLoading } = useSweetpassTier();
  const canUpgradeSweetpass =
    isSweetpassEnabled &&
    !isSweetpassTierLoading &&
    sweetpassTierName !== 'SWEETPASS_PLUS';

  // ─────────────────────────────────────────────────────────────────────

  return useCallback(() => {
    track('home.hero_module');

    if (action === Actions.ViewMenu) {
      handleViewMenu();

      return;
    }

    if (action === Actions.UpgradeSweetpass && canUpgradeSweetpass) {
      navigation.navigate('Modal', { screen: 'SweetpassUpgrade' });

      return;
    }

    if (action === Actions.ExploreBenefits && isSweetpassEnabled) {
      navigation.navigate('Modal', { screen: 'SweetpassBenefits' });

      return;
    }

    if (action === Actions.ExploreChallenges && isSweetpassEnabled) {
      navigation.navigate('MainTabs', {
        screen: 'LoyaltyTab',
        params: { screen: 'SweetpassHome', params: { target: 'challenges' } },
      });

      return;
    }

    if (action === Actions.ExploreRewards && isSweetpassEnabled) {
      navigation.navigate('MainTabs', {
        screen: 'LoyaltyTab',
        params: { screen: 'SweetpassHome', params: { target: 'rewards' } },
      });

      return;
    }

    handleViewMenu(); // default handler.
  }, [
    action,
    isSweetpassEnabled,
    canUpgradeSweetpass,
    navigation,
    track,
    handleViewMenu,
  ]);
};

// ─── Types / Constants ─────────────────────────────────────────────────

const Actions = {
  ViewMenu: 'view_menu',
  UpgradeSweetpass: 'upgrade_sweetpass',
  ExploreBenefits: 'explore_benefits',
  ExploreChallenges: 'explore_challenges',
  ExploreRewards: 'explore_rewards',
};

type ValueOf<T> = T[keyof T];
type HomeHeroAction = ValueOf<typeof Actions>;
